import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Reading a barcode from the camera (§11.1).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE CAMERA IS THE SECOND-BEST INPUT HERE, NOT THE FIRST.
 *
 * A handheld USB barcode scanner is a KEYBOARD: it types the digits and presses
 * Enter. On the office desktop — which is where the owner said he will work
 * first — a plain focused text input IS the scanner, with no camera, no
 * permission prompt and no decoding. That path is in `ScanDialog` and always
 * works, including as manual entry when someone reads the number off the page.
 *
 * This hook is the phone case: point the camera at the printed document.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY THERE IS NOW A DECODER IN THE BUNDLE, WHEN THE OLD NOTE HERE SAID THERE
 * WOULD NEVER BE ONE.
 *
 * The old note read: "NOTHING HERE IS A DEPENDENCY. A JavaScript decoder (zxing
 * and friends) would cover iOS … That trade is worth making only once we know an
 * iPhone is involved." An iPhone 14 Pro Max is involved (2026-08-17), so the
 * trade has been made, deliberately, with the owner's agreement.
 *
 * `BarcodeDetector` — the Shape Detection API this file has always called — is
 * a CHROMIUM feature. WebKit does not implement it, and on iOS every browser is
 * WebKit, Chrome included. So on that handset there was no detector, therefore
 * no camera offered, therefore not even a permission prompt. Opening the camera
 * was never the problem. DECODING was.
 *
 * `barcode-detector` is a PONYFILL of exactly this API — same constructor, same
 * `detect()`, zxing compiled to WebAssembly underneath. So the code below did
 * not have to learn a second decoder shape; it only had to learn where its
 * constructor comes from.
 *
 * TWO RULES GOVERN THAT, AND BOTH ARE LOAD-BEARING:
 *
 *   1. NATIVE WINS WHERE IT EXISTS. On Android Chrome the browser's own
 *      implementation is faster and costs no download, so it is used and the
 *      ponyfill is never even fetched. The check is `globalThis.BarcodeDetector`
 *      at call time, so if WebKit ever ships it, this file starts using it with
 *      no edit and the 1.07 MB download simply stops happening.
 *
 *   2. THE PONYFILL IS LOADED LAZILY — `await import(...)`, never a top-level
 *      import. It is a separate Rollup chunk plus a WASM fetch. The owner opens
 *      this app over mobile data in a workshop (§1.1) and most sessions never
 *      open the scanner at all; a static import would put that weight in the
 *      main bundle for every screen, every load, forever.
 *
 *   3. IT IS FETCHED FROM OUR OWN ORIGIN. zxing-wasm's default `locateFile`
 *      points at jsDelivr; see `ZXING_WASM_OVERRIDES` below for why that is not
 *      acceptable here and what replaces it.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/**
 * THE FORMATS THIS APP ACTUALLY PRINTS — and nothing else.
 *
 * `apps/api/src/services/pdf/barcode.ts` has exactly two encoders:
 * `documentBarcodeSvg` writes CODE 128 subset B (the document number), and
 * `documentQrSvg` writes a QR (the number plus date, both GSTINs and the
 * amount). Every bill, quotation, challan and credit note this app produces
 * carries one or both, and nothing carries anything else.
 *
 * The list used to include `code_39` and `ean_13` "for a customer's paperwork".
 * That was a guess about paper we do not print, and it is not free: the ponyfill
 * runs zxing over the frame once per requested family, so two speculative
 * formats made every frame slower on the one device that needs the speed. A
 * customer's own label is still scannable — with the handheld, into the text
 * box, which is the primary path anyway.
 */
export const SCAN_FORMATS = ["code_128", "qr_code"] as const;

/**
 * How often the PONYFILL is allowed to look at a frame, in milliseconds.
 *
 * The native detector is hardware-accelerated and is asked on every animation
 * frame. zxing-over-WASM is not: it decodes on the main thread, and running it
 * flat out heats a phone that is being held still against a printed page.
 * ~8 looks a second is far more than enough to catch a barcode a human is
 * aiming, and it leaves the CPU alone in between.
 */
const PONYFILL_DECODE_INTERVAL_MS = 120;

/**
 * How long the same decoded value is IGNORED after it has been handed over.
 *
 * The loop no longer stops when it finds something — see `tick()` — so without
 * this a barcode held in front of the lens would be delivered eight times a
 * second for the fraction of a second it takes React to close the dialog. One
 * and a half seconds is far longer than that and far shorter than the time it
 * takes a person to deliberately re-present the same document.
 */
const SCAN_REPEAT_SUPPRESS_MS = 1500;

/** The subset of the Shape Detection API this file uses. */
interface DetectedBarcode {
  rawValue: string;
  format: string;
}

interface BarcodeDetectorLike {
  detect: (source: CanvasImageSource) => Promise<DetectedBarcode[]>;
}

interface BarcodeDetectorConstructor {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
  getSupportedFormats?: () => Promise<string[]>;
}

/** Where a working detector came from — the browser, or the downloaded ponyfill. */
export interface LoadedDetector {
  Detector: BarcodeDetectorConstructor;
  /** `true` when this is the browser's own implementation. */
  native: boolean;
}

function nativeDetectorConstructor(): BarcodeDetectorConstructor | null {
  const candidate = (globalThis as { BarcodeDetector?: BarcodeDetectorConstructor })
    .BarcodeDetector;
  return typeof candidate === "function" ? candidate : null;
}

/**
 * The in-flight or settled ponyfill load, so a second `start()` — he tapped the
 * button twice — does not fetch the WASM twice.
 *
 * CLEARED ON FAILURE, deliberately. The likely failure is a workshop with no
 * signal, which is temporary; caching the rejection forever would mean the
 * camera stayed broken for the whole session after one bad moment, and the only
 * cure would be reloading the app.
 *
 * CLEARING THIS ALONE IS NOT ENOUGH, and that was a real defect: zxing-wasm
 * keeps its OWN cache — a `WeakMap` keyed by the module factory, written with
 * the promise whether it resolved or REJECTED — so after one failure every later
 * `prepareZXingModule` handed back the same rejection and the message stayed
 * forever. `purgeZXingModule()` in `loadPonyfill`'s catch is what actually makes
 * the retry above possible.
 */
let ponyfillLoad: Promise<LoadedDetector> | null = null;

/**
 * WHERE THE DECODER'S WebAssembly IS FETCHED FROM — our own origin, not a CDN.
 *
 * zxing-wasm ships a default `locateFile` that builds
 * `https://fastly.jsdelivr.net/npm/zxing-wasm@…/zxing_reader.wasm`. Three
 * reasons that cannot stand in this app:
 *
 *   • The SPA and the API share ONE origin on purpose (§12.5) — that is why
 *     there is no CORS middleware and no preflight anywhere. A billing app
 *     should not quietly acquire a third-party origin that serves it
 *     EXECUTABLE code, and the day a CSP goes in front of the SPA that fetch
 *     breaks outright.
 *   • It is unfixable when it fails. A workshop network that cannot reach
 *     jsDelivr — slow, carrier-filtered, DNS-blocked — gets "the barcode reader
 *     could not be downloaded" with nothing to repair on our server, because the
 *     file was never deployed there. Meanwhile the phone plainly CAN reach our
 *     origin: it just loaded the app from it.
 *   • It is 1.07 MB, not the "~200KB" an earlier comment here guessed. Worth
 *     serving from the host that is already warm and already HTTP-cached.
 *
 * The URL comes from `virtual:zxing-reader-wasm?url`, aliased in
 * `vite.config.ts` to the copy inside `barcode-detector`'s own dependency and
 * emitted as a build asset of the LAZY chunk. Imported dynamically, beside the
 * ponyfill, so nothing about it reaches the main bundle.
 *
 * ONE OBJECT, BUILT ONCE. zxing-wasm compares successive `overrides` key by key
 * with `===`; a fresh object (and therefore a fresh `locateFile` function) on
 * every call would compare unequal and re-instantiate the module each time.
 */
let zxingOverrides: { locateFile: () => string } | null = null;

async function loadPonyfill(): Promise<LoadedDetector> {
  const [module, wasm] = await Promise.all([
    import("barcode-detector/ponyfill"),
    import("virtual:zxing-reader-wasm?url"),
  ]);

  const wasmUrl = wasm.default;
  zxingOverrides ??= { locateFile: () => wasmUrl };

  /*
   * FETCH AND INSTANTIATE THE WASM *NOW*, rather than letting the first
   * `detect()` do it lazily.
   *
   * This is the whole reason the failure is reportable. Left lazy, a missing
   * WASM surfaces as `detect()` rejecting on frame after frame — inside the
   * per-frame `catch` that exists to ignore ONE bad frame — and the owner gets a
   * live camera pointed at a barcode that never resolves. The file's own warning
   * calls that "the worse failure, because it looks like it is working".
   *
   * Awaited here, a failed fetch is one rejection, before the camera is touched.
   */
  if (typeof module.prepareZXingModule === "function") {
    try {
      await module.prepareZXingModule({
        overrides: zxingOverrides,
        fireImmediately: true,
      });
    } catch (cause: unknown) {
      /*
       * EVICT zxing-wasm's OWN CACHE OF THE REJECTION. It stores the promise
       * regardless of how it settled, so without this the retry the comment on
       * `ponyfillLoad` promises is impossible: signal comes back, he taps again,
       * and the same rejected promise is handed straight back for the rest of
       * the session. `?.` because a test double need not implement it.
       */
      module.purgeZXingModule?.();
      throw cause;
    }
  }

  /*
   * One cast, at the boundary. The ponyfill's `DetectedBarcode` carries
   * `boundingBox` and `cornerPoints` we do not read, and its `detect()` accepts
   * a wider source union than `CanvasImageSource` — both differences are in the
   * safe direction, but neither is expressible without restating its types here.
   */
  return {
    Detector: module.BarcodeDetector as unknown as BarcodeDetectorConstructor,
    native: false,
  };
}

/**
 * A detector, from wherever one can be had.
 *
 * Resolves immediately with the browser's own where it exists — the ponyfill
 * module is not imported at all in that case, so nothing is downloaded. Rejects
 * only when the ponyfill chunk or its WASM cannot be fetched.
 */
export function loadBarcodeDetector(): Promise<LoadedDetector> {
  const native = nativeDetectorConstructor();
  if (native !== null) return Promise.resolve({ Detector: native, native: true });

  ponyfillLoad ??= loadPonyfill().catch((cause: unknown) => {
    ponyfillLoad = null;
    throw cause;
  });
  return ponyfillLoad;
}

/**
 * CAN THE CAMERA PATH BE OFFERED AT ALL ON THIS BROWSER?
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS FUNCTION MEANS CHANGED WHEN THE PONYFILL LANDED, AND THE CHANGE IS
 * THE POINT.
 *
 * It used to mean "is there a `BarcodeDetector` object right now" — a question
 * that answered `false` on every iPhone and hid the camera there. With a
 * decoder that can be FETCHED, that question no longer decides anything: the
 * honest answer to "can this browser decode?" became "yes, once something is
 * downloaded", which a synchronous boolean cannot say.
 *
 * So the boolean was narrowed to the part that IS synchronously knowable and
 * cannot change: CAN THIS PAGE OPEN A CAMERA? That is `getUserMedia` — absent
 * on a non-secure origin, in a locked-down WebView, and on a desktop browser
 * with the API disabled. Where it is absent no amount of downloading helps, and
 * `ScanDialog` correctly shows only the text box.
 *
 * The other half — "is a decoder ready?" — is now STATE on the hook rather than
 * a predicate, because it is a process with three outcomes: `preparing` while
 * the chunk and WASM are fetched, `error: "decoder"` when that fetch fails, and
 * `running` once a camera is up WITH a detector already in hand.
 *
 * THE INVARIANT THAT MATTERS IS UNCHANGED, AND IS NOW ENFORCED HARDER: a live
 * camera is never shown without a working decoder behind it. `start()` loads
 * the detector FIRST and only then asks for the camera, so the "looks like it is
 * working" failure cannot happen — a decoder that will not load means an
 * explanatory message and the text box, with no camera ever opened.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function cameraScanSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    navigator.mediaDevices !== undefined &&
    typeof navigator.mediaDevices.getUserMedia === "function"
  );
}

/**
 * Which step failed. A DISCRIMINANT, not a message: the browser's own
 * `getUserMedia` error text is untranslated English, and §11.4 forbids a
 * user-facing string that i18n never saw. The dialog maps these to `scan.*`.
 */
export type ScanFailure = "camera" | "decoder";

export interface BarcodeCamera {
  /** Can a camera be opened here at all? See `cameraScanSupported`. */
  supported: boolean;
  /** Fetching the decoder / opening the camera — nothing to point at yet. */
  preparing: boolean;
  /** A camera is live AND a decoder is in hand. */
  running: boolean;
  error: ScanFailure | null;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  start: () => Promise<void>;
  stop: () => void;
}

export function useBarcodeCamera(onScan: (value: string) => void): BarcodeCamera {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const frameRef = useRef<number | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ScanFailure | null>(null);

  const supported = cameraScanSupported();

  /*
   * THE TICKET FOR THE CURRENT ATTEMPT. `stop()` increments it, which is what
   * makes an attempt ABANDONABLE mid-flight.
   *
   * `start()` now awaits twice before a frame is ever read — the decoder, then
   * the permission prompt — and both can outlive the dialog. Without this, a
   * `getUserMedia` that resolves after the dialog closed would hand back a live
   * camera with nothing left to stop it, and the phone's camera light would
   * stay on. The same check inside the loop is why an in-flight `detect()`
   * cannot resurrect a cancelled loop by scheduling the next frame.
   */
  const runRef = useRef(0);

  /*
   * The callback is held in a ref so the scan loop never restarts because the
   * parent re-rendered. Restarting means re-acquiring the camera, which on a
   * phone is a visible black flash and a second of nothing.
   */
  const onScanRef = useRef(onScan);
  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  const stop = useCallback(() => {
    runRef.current += 1; // anything still awaiting is now stale
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    // EVERY track, explicitly. Dropping the reference is not enough: the camera
    // light stays on until the tracks are stopped, and a device that looks like
    // it is still watching is not something to leave behind.
    for (const track of streamRef.current?.getTracks() ?? []) track.stop();
    streamRef.current = null;
    setRunning(false);
    setPreparing(false);
  }, []);

  const start = useCallback(async () => {
    if (!supported) return;

    stop(); // cancel any previous attempt, and take the next ticket
    const ticket = runRef.current;
    const current = () => ticket === runRef.current;

    setError(null);
    setPreparing(true);

    /*
     * ── STEP 1: THE DECODER, BEFORE THE CAMERA ───────────────────────────────
     * Order is deliberate. Asking for the camera first would put the permission
     * prompt in front of the owner and then, sometimes, tell him the reader
     * could not be downloaded — having taken his permission for nothing and left
     * a stream to tidy up. This way a workshop with no signal costs one message
     * and no camera is ever opened.
     */
    let loaded: LoadedDetector;
    try {
      loaded = await loadBarcodeDetector();
    } catch {
      if (!current()) return;
      setPreparing(false);
      setError("decoder");
      return;
    }
    if (!current()) return;

    // ── STEP 2: THE CAMERA ───────────────────────────────────────────────────
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        // The REAR camera on a phone; ignored on a laptop with only one.
        video: { facingMode: "environment" },
        audio: false,
      });

      // He closed the dialog while the permission prompt was up. The stream is
      // ours now whether we wanted it or not, so it is ours to release.
      if (!current()) {
        for (const track of stream.getTracks()) track.stop();
        return;
      }
      streamRef.current = stream;

      const video = videoRef.current;
      if (video === null) {
        stop();
        return;
      }
      video.srcObject = stream;
      await video.play();
      if (!current()) return; // `stop()` already released the tracks

      setPreparing(false);
      setRunning(true);

      const detector = new loaded.Detector({ formats: [...SCAN_FORMATS] });
      const minimumGap = loaded.native ? 0 : PONYFILL_DECODE_INTERVAL_MS;
      let lastLook = 0;

      /*
       * THE LOOP DOES NOT STOP ITSELF WHEN IT FINDS SOMETHING, and that is a
       * FIX rather than a preference.
       *
       * It used to `return` without rescheduling after handing a value over,
       * trusting the caller to close the dialog. But the caller is entitled to
       * REJECT the value: `ScanDialog` drops anything that normalises to empty,
       * which is what a foreign label or a QR with an empty first field
       * produces. The dialog then stayed open with a live camera, a running
       * video and a loop that would never look at another frame — precisely the
       * "looks like it is working" failure this file exists to prevent, and only
       * escapable by closing the dialog.
       *
       * So the loop keeps looking, and the ONLY things that end it are the ones
       * that should: `stop()`, the dialog closing, and unmount — all three
       * observed through `current()`. These two variables are what stop an
       * accepted value being re-delivered eight times a second in the moment
       * before React gets round to closing the dialog.
       */
      let lastEmitted = "";
      let lastEmittedAt = 0;

      const tick = async () => {
        if (!current()) return;

        const element = videoRef.current;
        const now = Date.now();
        if (element === null || element.readyState < 2 || now - lastLook < minimumGap) {
          frameRef.current = requestAnimationFrame(() => void tick());
          return;
        }
        lastLook = now;

        try {
          const found = await detector.detect(element);
          /*
           * The dialog may have closed during the decode — a WASM decode is tens
           * of milliseconds, not zero. Checking AFTER the await is what stops the
           * loop scheduling itself again past `stop()`, which would hold the
           * camera open with no way left to cancel it.
           */
          if (!current()) return;
          const first = found[0]?.rawValue?.trim() ?? "";
          const repeat = first === lastEmitted && Date.now() - lastEmittedAt < SCAN_REPEAT_SUPPRESS_MS;
          if (first !== "" && !repeat) {
            lastEmitted = first;
            lastEmittedAt = Date.now();
            onScanRef.current(first);
            // The caller may have closed the dialog synchronously, which called
            // `stop()`. If it did, the ticket has moved on and there is nothing
            // left to schedule.
            if (!current()) return;
          }
        } catch {
          /* a single bad frame is not worth reporting — keep looking */
          if (!current()) return;
        }
        frameRef.current = requestAnimationFrame(() => void tick());
      };

      frameRef.current = requestAnimationFrame(() => void tick());
    } catch {
      /*
       * The common case is the person declining the permission prompt, which is
       * a decision rather than a fault. Either way the dialog still has the text
       * input, so this is a note and not a dead end.
       */
      if (!current()) return;
      stop();
      setError("camera");
    }
  }, [supported, stop]);

  // Release the camera if the component goes away without `stop()` — a route
  // change mid-scan would otherwise leave the light on.
  useEffect(() => stop, [stop]);

  return { supported, preparing, running, error, videoRef, start, stop };
}
